import { useState } from 'react';
import { Alert, Button, Checkbox, Grid, NumberInput, Select, Table, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';
import { ApiRequestError } from '../../../api/client';
import { parseNum } from '../../../lib/parseNum';
import { useActiveProducts, useAddRunOutput, useAllProducts, useDeleteRunOutput, useLocations } from '../api';
import { formatLineQty, formatPct, qtyInputLabel } from '../format';
import type { RunOutputLineDto } from '../types';

/** Outputs step of "Record a shift" / a kiln batch close (BUILD_SPEC.md §8.3:
 * "outputs (product, to-silo, tonnes, MC)"). Editable while the run is OPEN or
 * CLOSED. */
export function RunOutputsEditor({
  runId,
  siteId,
  lines,
  editable,
}: {
  runId: string;
  siteId: string;
  lines: RunOutputLineDto[];
  editable: boolean;
}) {
  const locationsQuery = useLocations(siteId);
  // Picker offers ACTIVE products only; the lookup map covers ALL products so an
  // already-recorded line for a since-deactivated product still resolves its
  // name/baseUom (matching the server's un-filtered products join — see
  // api.ts's useAllProducts doc).
  const productsQuery = useActiveProducts();
  const allProductsQuery = useAllProducts();
  const addOutput = useAddRunOutput(runId);
  const deleteOutput = useDeleteRunOutput(runId);

  const [productId, setProductId] = useState<string | null>(null);
  const [toLocationId, setToLocationId] = useState<string | null>(null);
  const [qtyKg, setQtyKg] = useState<number | string>('');
  const [mcPct, setMcPct] = useState<number | string>('');
  const [handlingUnits, setHandlingUnits] = useState<number | string>('');
  const [isPrimary, setIsPrimary] = useState(false);

  const locations = locationsQuery.data?.items ?? [];
  const products = productsQuery.data?.items ?? [];
  const locationsById = new Map(locations.map((l) => [l.id, l]));
  const productsById = new Map((allProductsQuery.data?.items ?? []).map((p) => [p.id, p]));
  // Drives the entry form's quantity label (below) — the SAME baseUom lookup the
  // table rows use for their per-line unit display.
  const selectedProduct = productId ? productsById.get(productId) : undefined;

  const parsedQtyKg = parseNum(qtyKg);
  const parsedMcPct = parseNum(mcPct);
  const parsedHandlingUnits = parseNum(handlingUnits);

  function reset() {
    setProductId(null);
    setToLocationId(null);
    setQtyKg('');
    setMcPct('');
    setHandlingUnits('');
    setIsPrimary(false);
    addOutput.reset();
  }

  function handleAdd() {
    if (!productId || !toLocationId || parsedQtyKg === null) return;
    addOutput.mutate(
      {
        productId,
        toLocationId,
        qtyKg: parsedQtyKg.toFixed(3),
        mcPct: parsedMcPct === null ? null : parsedMcPct.toFixed(2),
        handlingUnits: parsedHandlingUnits,
        isPrimary,
      },
      { onSuccess: reset },
    );
  }

  return (
    <>
      <Title order={4} mb="xs">
        Outputs
      </Title>
      {editable && (
        <Grid align="flex-end" mb="sm">
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Select
              label="Product"
              placeholder="Product"
              data={products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))}
              value={productId}
              onChange={setProductId}
              searchable
              disabled={productsQuery.isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Select
              label="To silo/location"
              placeholder="Location"
              data={locations.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))}
              value={toLocationId}
              onChange={setToLocationId}
              searchable
              disabled={locationsQuery.isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 2 }}>
            <NumberInput
              label={qtyInputLabel(selectedProduct?.baseUom, 'Tonnes (kg)', 'Qty (units)')}
              decimalScale={3}
              min={0}
              value={qtyKg}
              onChange={setQtyKg}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 2 }}>
            <NumberInput
              label="Moisture %"
              decimalScale={2}
              min={0}
              max={100}
              value={mcPct}
              onChange={setMcPct}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 1 }}>
            <NumberInput
              label="Cages"
              decimalScale={0}
              min={0}
              value={handlingUnits}
              onChange={setHandlingUnits}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 6, sm: 1 }}>
            <Checkbox mt={24} label="Primary" checked={isPrimary} onChange={(e) => setIsPrimary(e.currentTarget.checked)} />
          </Grid.Col>
          <Grid.Col span={12}>
            <Button
              size="xs"
              variant="light"
              disabled={!productId || !toLocationId || parsedQtyKg === null}
              loading={addOutput.isPending}
              onClick={handleAdd}
            >
              Add output
            </Button>
          </Grid.Col>
        </Grid>
      )}
      {addOutput.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} mb="sm">
          {addOutput.error instanceof ApiRequestError ? addOutput.error.message : 'Failed to add output'}
        </Alert>
      )}
      <Table.ScrollContainer minWidth={600}>
        <Table verticalSpacing="xs" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th>To</Table.Th>
              {/* Neutral header — a run can mix KG and EACH products, so the unit
                  is shown per-row (formatLineQty) instead of a single "(kg)"
                  header that would lie for an EACH line. */}
              <Table.Th>Qty</Table.Th>
              <Table.Th>MC%</Table.Th>
              <Table.Th>Cages</Table.Th>
              <Table.Th>Primary</Table.Th>
              {editable && <Table.Th />}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lines.map((line) => (
              <Table.Tr key={line.id}>
                <Table.Td>{productsById.get(line.productId)?.name ?? line.productId}</Table.Td>
                <Table.Td>{locationsById.get(line.toLocationId)?.name ?? line.toLocationId}</Table.Td>
                <Table.Td>{formatLineQty(line.qtyKg, productsById.get(line.productId)?.baseUom)}</Table.Td>
                <Table.Td>{formatPct(line.mcPct)}</Table.Td>
                <Table.Td>{line.handlingUnits ?? '—'}</Table.Td>
                <Table.Td>{line.isPrimary ? 'Yes' : ''}</Table.Td>
                {editable && (
                  <Table.Td>
                    <Button variant="subtle" color="red" size="compact-sm" onClick={() => deleteOutput.mutate(line.id)}>
                      <IconTrash size={14} />
                    </Button>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {lines.length === 0 && (
          <Text c="dimmed" size="sm" mt="xs">
            No outputs recorded yet.
          </Text>
        )}
      </Table.ScrollContainer>
    </>
  );
}
